import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "com.oplena.web";

// Apply user language preference on login
const applyUserLanguage = (userData) => {
    const lang = userData?.language || userData?.owner?.language;
    if (lang) {
        localStorage.setItem("I18N_LANGUAGE", lang);
        localStorage.setItem("i18nextLng", lang);
    }
};

/* ───────── Design Tokens (Oplena theme) ───────── */
const PRIMARY = "#6a2dec";
const BG = "hsl(220, 20%, 7%)";
const BG_CARD = "hsl(220, 18%, 10%)";
const TEXT = "hsl(0, 0%, 92%)";
const TEXT_MUTED = "hsl(220, 10%, 55%)";
const GLASS_BG = "hsla(220, 18%, 12%, 0.7)";
const GLASS_BORDER = "hsla(220, 15%, 25%, 0.5)";
const GRADIENT_CTA = "linear-gradient(135deg, #8b3df0 0%, #6a2dec 100%)";
const FONT_DISPLAY = "'Space Grotesk', 'Inter', sans-serif";
const FONT_BODY = "'Inter', 'Segoe UI', sans-serif";

/* ───────── Icons ───────── */
const IconBattery = ({ size = 24 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="16" height="10" x="2" y="7" rx="2" ry="2" /><line x1="22" x2="22" y1="11" y2="13" />
    </svg>
);
const IconZap = ({ size = 16 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
    </svg>
);

const OwnerLogin = () => {
    document.title = "Login | Oplena";
    const navigate = useNavigate();
    const [isRegister, setIsRegister] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [slideIndex, setSlideIndex] = useState(0);

    const [form, setForm] = useState({
        name: "", email: "", password: "", phone: "", document: "",
    });

    useEffect(() => {
        const checkStorage = (key) => {
            const stored = localStorage.getItem(key);
            if (!stored) return false;
            try {
                const data = JSON.parse(stored);
                if (data?.token && data?.expires_in_date > Date.now()) return true;
            } catch {}
            localStorage.removeItem(key);
            return false;
        };
        if (checkStorage("owner") || checkStorage("users")) {
            window.location.replace("/admin/dashboard");
        }
    }, []);

    // Auto-rotate slides
    useEffect(() => {
        const timer = setInterval(() => setSlideIndex(i => (i + 1) % 3), 4000);
        return () => clearInterval(timer);
    }, []);

    // Handle social login response from API
    const handleSocialToken = useCallback(async (provider, idToken, name, email) => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            const res = await fetch(`${process.env.REACT_APP_URL_API}auth/social/${provider}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id_token: idToken, name, email }),
            });
            const json = await res.json();
            if (res.ok && json?.data?.token) {
                const data = json.data;
                applyUserLanguage(data.user);
                const role = data.user?.role || "user";
                const userWithRole = { ...data.user, role };
                localStorage.setItem("owner", JSON.stringify({ token: data.token, owner: userWithRole, expires_in_date: Date.now() + 7 * 86400000 }));
                localStorage.setItem("users", JSON.stringify({ token: data.token, user: userWithRole, expires_in_date: Date.now() + 7 * 86400000 }));
                if (data.user.avatar) localStorage.setItem("owner_avatar", data.user.avatar);
                window.location.href = "/admin/dashboard";
            } else {
                setErrorMessage(json?.message || `Erro ao autenticar com ${provider}`);
            }
        } catch {
            setErrorMessage("Erro de conexão");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load Google Identity Services
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.onload = () => {
            window.google?.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: (response) => handleSocialToken("google", response.credential),
            });
        };
        document.head.appendChild(script);
        return () => { try { document.head.removeChild(script); } catch {} };
    }, [handleSocialToken]);

    const handleGoogleLogin = () => {
        if (window.google?.accounts?.id) {
            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    // Fallback: use popup mode
                    window.google.accounts.id.renderButton(
                        document.createElement("div"),
                        { type: "standard" }
                    );
                    // Try with popup
                    const client = window.google.accounts.oauth2.initTokenClient({
                        client_id: GOOGLE_CLIENT_ID,
                        scope: "email profile",
                        callback: async (tokenResponse) => {
                            // Exchange access token for user info then use id_token
                            const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
                                headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
                            });
                            const info = await res.json();
                            handleSocialToken("google", tokenResponse.access_token, info.name, info.email);
                        },
                    });
                    client.requestAccessToken();
                }
            });
        } else if (!GOOGLE_CLIENT_ID) {
            setErrorMessage("Google Client ID não configurado. Adicione REACT_APP_GOOGLE_CLIENT_ID no .env");
        }
    };

    const handleAppleLogin = () => {
        // Load Apple JS SDK and trigger login
        const loadAndLogin = () => {
            if (!window.AppleID) {
                const script = document.createElement("script");
                script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
                script.onload = () => triggerAppleLogin();
                document.head.appendChild(script);
            } else {
                triggerAppleLogin();
            }
        };

        const triggerAppleLogin = () => {
            window.AppleID.auth.init({
                clientId: APPLE_CLIENT_ID,
                scope: "name email",
                redirectURI: window.location.origin + "/login",
                usePopup: true,
            });
            window.AppleID.auth.signIn().then((response) => {
                const idToken = response?.authorization?.id_token;
                const userData = response?.user;
                const name = userData ? `${userData.name?.firstName || ""} ${userData.name?.lastName || ""}`.trim() : undefined;
                const email = userData?.email;
                if (idToken) handleSocialToken("apple", idToken, name, email);
            }).catch((err) => {
                if (err?.error !== "popup_closed_by_user") {
                    setErrorMessage("Erro ao autenticar com Apple");
                }
            });
        };

        loadAndLogin();
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        if (fieldErrors[e.target.name]) setFieldErrors({ ...fieldErrors, [e.target.name]: "" });
    };

    const validate = () => {
        const errors = {};
        if (!form.email) errors.email = "E-mail é obrigatório";
        else if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = "E-mail inválido";
        if (!form.password) errors.password = "Senha é obrigatória";
        else if (form.password.length < 6) errors.password = "Mínimo 6 caracteres";
        if (isRegister && !form.name) errors.name = "Nome é obrigatório";
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setIsLoading(true);
        setErrorMessage("");
        try {
            const payload = isRegister ? form : { email: form.email.trim(), password: form.password };

            if (isRegister) {
                // Register as owner
                const res = await fetch(process.env.REACT_APP_URL_API + "owner/register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
                const json = await res.json();
                if (!res.ok || json?.code >= 400) { setErrorMessage(json?.message || "Erro ao processar"); setIsLoading(false); return; }
                const data = json?.data;
                if (data?.token) {
                    applyUserLanguage(data.owner);
                    localStorage.setItem("owner", JSON.stringify({ token: data.token, owner: data.owner, expires_in_date: Date.now() + 7 * 86400000 }));
                    localStorage.setItem("users", JSON.stringify({ token: data.token, user: { ...data.owner, role: "owner" }, expires_in_date: Date.now() + 7 * 86400000 }));
                    window.location.href = "/admin/dashboard";
                    return;
                }
            } else {
                // Unified login - single endpoint handles both users and owners
                const apiUrl = process.env.REACT_APP_URL_API || "/api/v1/";
                const res = await fetch(`${apiUrl}auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
                const json = await res.json();
                if (res.ok && json?.data?.token) {
                    const data = json.data;
                    // Check if response is owner (has "owner" key) or user (has "user" key)
                    if (data.owner) {
                        applyUserLanguage(data.owner);
                        localStorage.setItem("owner", JSON.stringify({ token: data.token, owner: data.owner, expires_in_date: Date.now() + 7 * 86400000 }));
                        localStorage.setItem("users", JSON.stringify({ token: data.token, user: { ...data.owner, role: "owner" }, expires_in_date: Date.now() + 7 * 86400000 }));
                    } else {
                        applyUserLanguage(data.user);
                        const role = data.user?.role || "user";
                        localStorage.setItem("users", JSON.stringify({ token: data.token, user: { ...data.user, role }, expires_in_date: Date.now() + 7 * 86400000 }));
                        if (role === "owner") {
                            localStorage.setItem("owner", JSON.stringify({ token: data.token, owner: data.user, expires_in_date: Date.now() + 7 * 86400000 }));
                        }
                    }
                    window.location.href = "/admin/dashboard";
                    return;
                }

                setErrorMessage(json?.message || "E-mail ou senha inválidos");
            }
        } catch (error) {
            setErrorMessage("Erro ao processar");
        } finally {
            setIsLoading(false);
        }
    };

    const slides = [
        { stat: "+8", label: "Produtos", quote: "Um ecossistema de soluções do Grupo Oplena." },
        { stat: "24/7", label: "Disponível", quote: "Seu painel acessível a qualquer hora, em qualquer lugar." },
        { stat: "100%", label: "Seguro", quote: "Transações protegidas e dados criptografados." },
    ];

    const inputStyle = {
        width: "100%",
        background: "hsla(220,18%,15%,0.8)",
        border: `1px solid ${GLASS_BORDER}`,
        borderRadius: 12,
        color: TEXT,
        padding: "13px 16px",
        fontSize: "0.9rem",
        outline: "none",
        fontFamily: FONT_BODY,
        transition: "border-color 0.3s",
    };

    const labelStyle = {
        color: TEXT_MUTED,
        fontSize: "0.8rem",
        fontWeight: 600,
        marginBottom: 6,
        display: "block",
        fontFamily: FONT_DISPLAY,
    };

    return (
        <div style={{
            minHeight: "100vh", background: BG,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, fontFamily: FONT_BODY,
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');
                input::placeholder { color: ${TEXT_MUTED} !important; }
                input:focus { border-color: ${PRIMARY} !important; }
            `}</style>

            <div style={{
                display: "flex", maxWidth: 960, width: "100%", borderRadius: 24, overflow: "hidden",
                boxShadow: "0 8px 32px hsla(0,0%,0%,0.4)", border: `1px solid ${GLASS_BORDER}`,
            }}>
                {/* ═══ LEFT - Branding ═══ */}
                <div style={{
                    flex: "1 1 420px", minHeight: 520,
                    background: GLASS_BG, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                    padding: "48px 40px", display: "flex", flexDirection: "column", justifyContent: "space-between",
                    position: "relative", overflow: "hidden", borderRight: `1px solid ${GLASS_BORDER}`,
                }}>
                    {/* Glow effects */}
                    <div style={{ position: "absolute", top: -80, right: -80, width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, hsla(258,84%,60%,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", bottom: -60, left: -60, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, hsla(262,83%,55%,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

                    {/* Logo */}
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 40 }}>
                            <img src="/oplena-logo-light.png" alt="Oplena" style={{ height: 38, display: "block" }} />
                        </div>
                        <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "2rem", margin: 0, lineHeight: 1.2, color: "hsl(0, 0%, 88%)" }}>Painel do Grupo Oplena</h2>
                        <h3 style={{ color: PRIMARY, fontWeight: 400, fontSize: "1.4rem", margin: "8px 0 0", fontStyle: "italic" }}>Tecnologia que move negócios</h3>
                    </div>

                    {/* Stats carousel */}
                    <div>
                        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                            {slides.map((s, i) => (
                                <div key={i} style={{
                                    flex: 1, padding: "14px 8px", textAlign: "center", borderRadius: 12,
                                    background: slideIndex === i ? "hsla(258,84%,60%,0.12)" : "hsla(220,18%,15%,0.5)",
                                    border: `1px solid ${slideIndex === i ? "hsla(258,84%,60%,0.25)" : GLASS_BORDER}`,
                                    transition: "all 0.4s ease",
                                }}>
                                    <div style={{ color: PRIMARY, fontWeight: 700, fontSize: "1.1rem", fontFamily: FONT_DISPLAY }}>{s.stat}</div>
                                    <div style={{ color: TEXT_MUTED, fontSize: "0.7rem", marginTop: 2 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Slide dots */}
                        <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 16 }}>
                            {slides.map((_, i) => (
                                <div key={i} onClick={() => setSlideIndex(i)} style={{
                                    width: slideIndex === i ? 24 : 8, height: 8, borderRadius: 4,
                                    background: slideIndex === i ? PRIMARY : GLASS_BORDER,
                                    transition: "all 0.3s ease", cursor: "pointer",
                                }} />
                            ))}
                        </div>

                        <p style={{ color: "hsla(45,10%,90%,0.35)", fontSize: "0.8rem", fontStyle: "italic", margin: 0, borderLeft: `2px solid hsla(258,84%,60%,0.3)`, paddingLeft: 12, transition: "opacity 0.4s", minHeight: 40 }}>
                            "{slides[slideIndex].quote}"
                        </p>
                    </div>
                </div>

                {/* ═══ RIGHT - Form ═══ */}
                <div style={{
                    flex: "1 1 420px", background: BG_CARD,
                    padding: "48px 40px", display: "flex", flexDirection: "column", justifyContent: "center",
                }}>
                    <h2 style={{ fontFamily: FONT_DISPLAY, color: PRIMARY, fontWeight: 700, fontSize: "1.8rem", marginBottom: 6 }}>
                        {isRegister ? "Criar Conta" : "Bem vindo!"}
                    </h2>
                    <p style={{ color: TEXT_MUTED, fontSize: "0.9rem", marginBottom: 32 }}>
                        {isRegister ? "Preencha os dados para se cadastrar" : <>Faça o login para continuar — <span style={{ color: PRIMARY, fontWeight: 600 }}>Oplena</span></>}
                    </p>

                    <form onSubmit={handleSubmit}>
                        {isRegister && (
                            <>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={labelStyle}>Nome</label>
                                    <input style={inputStyle} type="text" name="name" value={form.name} onChange={handleChange} placeholder="Digite seu nome completo" />
                                    {fieldErrors.name && <small style={{ color: "#f87171", fontSize: "0.78rem" }}>{fieldErrors.name}</small>}
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={labelStyle}>Telefone</label>
                                    <input style={inputStyle} type="text" name="phone" value={form.phone} onChange={handleChange} placeholder="(00) 00000-0000" />
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={labelStyle}>CPF/CNPJ</label>
                                    <input style={inputStyle} type="text" name="document" value={form.document} onChange={handleChange} placeholder="Digite seu CPF ou CNPJ" />
                                </div>
                            </>
                        )}

                        <div style={{ marginBottom: 16 }}>
                            <label style={labelStyle}>Login</label>
                            <input style={inputStyle} type="text" name="email" value={form.email} onChange={handleChange} placeholder="Digite seu Email, CPF ou CNPJ" required />
                            {fieldErrors.email && <small style={{ color: "#f87171", fontSize: "0.78rem" }}>{fieldErrors.email}</small>}
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label style={labelStyle}>Senha</label>
                            <input style={inputStyle} type="password" name="password" value={form.password} onChange={handleChange} placeholder="Digite a senha" required />
                            {fieldErrors.password && <small style={{ color: "#f87171", fontSize: "0.78rem" }}>{fieldErrors.password}</small>}
                        </div>

                        {errorMessage && (
                            <div style={{ background: "hsla(0,84%,60%,0.1)", border: "1px solid hsla(0,84%,60%,0.3)", borderRadius: 10, padding: "10px 14px", color: "#f87171", fontSize: "0.85rem", marginBottom: 16 }}>
                                {errorMessage}
                            </div>
                        )}

                        <button type="submit" disabled={isLoading} style={{
                            width: "100%", padding: 14, background: GRADIENT_CTA, color: BG, border: "none", borderRadius: 12,
                            fontSize: "1rem", fontWeight: 700, fontFamily: FONT_DISPLAY, cursor: isLoading ? "not-allowed" : "pointer",
                            boxShadow: "0 4px 20px hsla(258,84%,60%,0.3)", transition: "transform 0.2s, opacity 0.2s",
                            marginTop: 8, opacity: isLoading ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        }}
                            onMouseEnter={e => { if (!isLoading) e.currentTarget.style.transform = "translateY(-1px)"; }}
                            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                        >
                            <IconZap size={18} />
                            {isLoading ? "Aguarde..." : (isRegister ? "Criar conta" : "Entrar")}
                        </button>
                    </form>

                    {!isRegister && (
                        <>
                            <div style={{ textAlign: "center", marginTop: 16 }}>
                                <a href="/forgot-password" style={{ color: PRIMARY, fontSize: "0.85rem", textDecoration: "none" }}>
                                    Esqueceu sua senha?
                                </a>
                            </div>

                        </>
                    )}

                    <div style={{ textAlign: "center", marginTop: 20 }}>
                        <span style={{ color: TEXT_MUTED, fontSize: "0.9rem" }}>
                            {isRegister ? "Já tem conta? " : "Não tem conta? "}
                        </span>
                        <a href="#" onClick={e => { e.preventDefault(); setIsRegister(!isRegister); setErrorMessage(""); setFieldErrors({}); }}
                            style={{ color: PRIMARY, fontWeight: 600, fontSize: "0.9rem", textDecoration: "underline" }}>
                            {isRegister ? "Entrar" : "Cadastrar"}
                        </a>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ position: "fixed", bottom: 16, left: 0, right: 0, textAlign: "center", color: "hsla(45,10%,90%,0.25)", fontSize: "0.78rem" }}>
                © 2026 Oplena. Todos os direitos reservados.
            </div>
        </div>
    );
};

export default OwnerLogin;
